// dev-navbar — Marqo-powered predictive search for the navbar search box.
//
// Mounted by sections/dev-navbar.liquid as <dev-predictive-search>. A plain
// theme asset (NOT part of the app/scripts esbuild graph), so — like
// dev-collection.js — it drives results off the *public* window.Marqo API
// (published by assets/marqo.js, gated on settings.marqo_enabled):
//
//   Marqo.search(body)                       — res.hits + res.facets + totalHits
//   Marqo.recommendations.queries({q, limit}) — { suggestions } term completions
//   Marqo.buildCard(hits, cardEl)            — hits[] → DocumentFragment of cards
//   Marqo.DEFAULT_FACETS / Marqo.labelFor    — facet config + labels
//
// Renders a megapanel: a rail (live term Suggestions, Recent Searches, and
// Category / Brand facets) + a 3-up product grid via buildCard. Recent searches
// persist in localStorage and work without Marqo. Degrades to the native form
// submit when Marqo is unavailable.

(function () {
  'use strict';

  const ELEMENT_NAME = 'dev-predictive-search';
  const MIN_CHARS = 1;        // start suggesting from the first letter
  const HAS_TERM = /[\p{L}\p{N}]/u; // a real query has ≥1 letter/digit — block symbol-only junk
  const SUGGEST_MAX = 6;      // term suggestions shown
  const SUGGEST_POOL = 40;    // products fetched to mine fallback terms from (grid still shows `data-limit`)
  const RECENT_KEY = 'dev-recent-searches';
  const RECENT_MAX = 5;

  // Rail facet groups — only Category + Brand (by Marqo field name, with the
  // display title the design uses).
  const RAIL_FACETS = [
    { field: 'productMetafieldCustomLevel1Categories', title: 'Category' },
    { field: 'productVendor', title: 'Brand' },
  ];

  // attributesToRetrieve whitelist — the fields buildCard reads (Marqo docs can
  // be ~1MB each; without this every call pulls full documents).
  const ATTRS = [
    '_id', 'parentProductId', 'productTitle', 'productTitle_fr', 'productHandle', 'productHandle_fr',
    'variantImageUrl', 'productImageUrls', 'price', 'priceMin', 'priceMax',
    'comparePrice', 'promoPrice', 'promoPriceMin', 'promoPriceMax', 'maxDiscount', 'maxPromoDiscount',
    'mfield_custom_call_for_price', 'productOutOfStock',
  ];

  // ── money formatting (mirrors dev-collection.js; price.js calls el.set(cents)) ──
  let MONEY_FORMAT = '${{amount}}';
  let MONEY_CURRENCY = 'USD';

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = precision == null ? 2 : precision;
    thousands = thousands == null ? ',' : thousands;
    decimal = decimal == null ? '.' : decimal;
    const value = (Number(number) || 0).toFixed(precision);
    const parts = value.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    const cents = parts[1] ? decimal + parts[1] : '';
    return dollars + cents;
  }

  function formatMoney(cents) {
    const amount = (Number(cents) || 0) / 100;
    const fmt = MONEY_FORMAT;
    if (!fmt || !/\{\{\s*\w+\s*\}\}/.test(fmt)) {
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: MONEY_CURRENCY || 'USD' }).format(amount);
      } catch (_) {
        return '$' + formatWithDelimiters(amount, 2);
      }
    }
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_m, name) {
      switch (name) {
        case 'amount': return formatWithDelimiters(amount, 2, ',', '.');
        case 'amount_no_decimals': return formatWithDelimiters(amount, 0, ',', '.');
        case 'amount_with_comma_separator': return formatWithDelimiters(amount, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return formatWithDelimiters(amount, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return formatWithDelimiters(amount, 2, "'", '.');
        case 'amount_no_decimals_with_space_separator': return formatWithDelimiters(amount, 0, ' ', '.');
        case 'amount_with_space_separator': return formatWithDelimiters(amount, 2, ' ', ',');
        default: return formatWithDelimiters(amount, 2, ',', '.');
      }
    });
  }

  if (!customElements.get('money-set')) {
    customElements.define('money-set', class extends HTMLElement {
      set(cents) {
        const n = Number(cents);
        if (!Number.isFinite(n)) return;
        if (n <= 0) {
          this.textContent = '';
          this.closest('.dev-plp-card__price')?.classList.add('dev-plp-card__price--unpriced');
          this.closest('.dev-ps-card__price')?.classList.add('dev-ps-card__price--unpriced');
          return;
        }
        this.textContent = formatMoney(n);
      }
    });
  }

  // ── helpers ─────────────────────────────────────────────────────────
  function marqo() { return window.Marqo; }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  const escapeAttr = escapeHtml;

  // Read a facet bucket as [value, info] entries — mirrors dev-collection.js.
  function facetEntries(bucket) {
    if (!bucket || typeof bucket !== 'object') return [];
    const keys = Object.keys(bucket);
    if (keys.length === 1 && keys[0] === 'value' && bucket.value
        && typeof bucket.value === 'object' && !('count' in bucket.value)) {
      return Object.entries(bucket.value);
    }
    return Object.entries(bucket);
  }

  // ── recent searches (localStorage; works without Marqo) ──────────────
  function readRecent() {
    try { return JSON.parse(localStorage.getItem(RECENT_KEY)) || []; } catch (_) { return []; }
  }
  function pushRecent(term) {
    term = String(term || '').trim();
    if (!term) return;
    const list = readRecent().filter((x) => x.toLowerCase() !== term.toLowerCase());
    list.unshift(term);
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX))); } catch (_) { /* quota/private mode */ }
  }

  if (!customElements.get(ELEMENT_NAME)) {
    customElements.define(ELEMENT_NAME, class extends HTMLElement {
      #input = null;
      #pillInput = null;    // the modal's own search field (covers the navbar input)
      #pillClear = null;
      #panel = null;
      #grid = null;
      #empty = null;
      #scrim = null;
      #cardTemplate = null;
      #dict = null;         // cached catalog-wide facet-value dictionary [{term,count}]
      #dictPromise = null;
      #reqSeq = 0;          // monotonic token so a stale response can't overwrite a newer one
      #activeIndex = -1;    // keyboard-highlighted result
      #onDocClick = null;
      #onReflow = null;     // resize/scroll → re-cap panel height to the viewport
      #mobileTrigger = null;
      #onMobileOpen = null; // mobile header AI-search button → open the modal

      connectedCallback() {
        if (this.dataset.moneyFormat) MONEY_FORMAT = this.dataset.moneyFormat;
        if (this.dataset.currency) MONEY_CURRENCY = this.dataset.currency;

        this.#input = this.querySelector('[data-search-input]');
        this.#pillInput = this.querySelector('[data-ps-input]');
        this.#pillClear = this.querySelector('[data-ps-clear]');
        this.#panel = this.querySelector('[data-results-panel]');
        this.#grid = this.querySelector('[data-results]');
        this.#empty = this.querySelector('[data-empty]');
        this.#scrim = this.querySelector('[data-ps-scrim]');
        this.#cardTemplate = this.querySelector('template[data-result-card]')
          ?.content?.querySelector('.marqo-product-card') || null;
        if (!this.#input || !this.#panel || !this.#grid || !this.#cardTemplate) return;

        const run = this.#debounce((q) => this.#search(q), 200);

        // Typing in either input drives the same search; keep both in sync so the
        // covered navbar input still holds the value (recent searches / form submit).
        const onType = (el) => {
          this.#syncInputs(el);
          const q = el.value.trim();
          // Symbol-only / empty → don't run a junk query; just show the default panel.
          if (q.length < MIN_CHARS || !HAS_TERM.test(q)) { this.#showDefault(); return; }
          run(q);
        };

        // Navbar input = the trigger: focusing it opens the modal (which covers the
        // navbar) and hands typing to the pill field inside the panel.
        this.#input.addEventListener('focus', () => {
          this.#syncInputs(this.#input);
          const q = this.#input.value.trim();
          // Opening moves focus to the pill (see #open); showDefault/run open the panel.
          if (q.length >= MIN_CHARS && HAS_TERM.test(q)) run(q); else this.#showDefault();
        });
        this.#input.addEventListener('input', () => onType(this.#input));
        this.#input.addEventListener('keydown', (e) => this.#onKeydown(e));

        // Pill field = the working input while the modal is open.
        if (this.#pillInput) {
          this.#pillInput.addEventListener('input', () => onType(this.#pillInput));
          this.#pillInput.addEventListener('keydown', (e) => this.#onKeydown(e));
        }
        if (this.#pillClear) this.#pillClear.addEventListener('click', () => {
          const hadValue = String((this.#pillInput && this.#pillInput.value) || this.#input.value || '').trim() !== '';
          if (this.#pillInput) this.#pillInput.value = '';
          this.#input.value = '';
          // Empty already → the ✕ closes the dropdown; otherwise it just clears the query.
          if (!hadValue) { this.#close(); this.#input.blur(); return; }
          this.#showDefault();
          if (this.#pillInput) this.#pillInput.focus();
        });

        // Clicking the scrim (over the topbar / page behind the panel) closes the modal.
        if (this.#scrim) this.#scrim.addEventListener('click', () => this.#close());

        // The navbar search button submits the form; block symbol-only / empty and
        // record the term. (Enter is handled in #onKeydown so it works from the pill too.)
        this.querySelector('form')?.addEventListener('submit', (e) => {
          const term = this.#input.value.trim();
          if (!HAS_TERM.test(term)) {
            e.preventDefault();
            this.#input.value = term; // reset stray whitespace
            this.#input.setCustomValidity('Please enter letters or numbers to search.');
            this.#input.reportValidity();
            this.#input.addEventListener('input', () => this.#input.setCustomValidity(''), { once: true });
            return;
          }
          this.#input.value = term; // search the trimmed term, not leading/trailing spaces
          this.#input.setCustomValidity('');
          pushRecent(term);
        });
        this.#panel.addEventListener('click', (e) => {
          const link = e.target.closest('a[href]');
          if (!link) return;
          pushRecent(link.dataset.suggestTerm || this.#input.value);
        });

        this.#onDocClick = (e) => { if (!this.contains(e.target)) this.#close(); };
        document.addEventListener('click', this.#onDocClick);

        this.#onReflow = () => this.#sizePanel();
        window.addEventListener('resize', this.#onReflow);
        window.addEventListener('scroll', this.#onReflow, { passive: true });

        // Mobile header AI-search button (outside this element — the mobile bar hides
        // the navbar search input). stopPropagation so the document click-outside
        // handler above doesn't immediately re-close the modal we just opened.
        this.#mobileTrigger = document.querySelector('[data-ps-mobile-open]');
        if (this.#mobileTrigger) {
          this.#onMobileOpen = (e) => { e.stopPropagation(); this.openSearch(); };
          this.#mobileTrigger.addEventListener('click', this.#onMobileOpen);
        }
      }

      disconnectedCallback() {
        if (this.#onDocClick) document.removeEventListener('click', this.#onDocClick);
        if (this.#onReflow) {
          window.removeEventListener('resize', this.#onReflow);
          window.removeEventListener('scroll', this.#onReflow);
        }
        if (this.#mobileTrigger && this.#onMobileOpen) {
          this.#mobileTrigger.removeEventListener('click', this.#onMobileOpen);
        }
      }

      // Public: open the predictive-search modal in its default (empty-query) state.
      // Used by the mobile header's AI-search button. The modal is a fixed full-screen
      // overlay with its own pill input, so it works with no visible navbar search bar.
      openSearch() {
        this.#showDefault();
      }

      #debounce(fn, ms) {
        let t;
        return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); };
      }

      // Mirror a value between the navbar input and the modal's pill input so both
      // always agree (the covered navbar input still drives form submit / recent).
      #syncInputs(src) {
        const val = src.value;
        if (src !== this.#input) this.#input.value = val;
        if (this.#pillInput && src !== this.#pillInput) this.#pillInput.value = val;
      }

      // mode: 'typing' (live, rail shows term Suggestions) or 'default' (empty
      // focus, rail shows the configured Popular Searches + Recent Searches).
      async #search(q, mode = 'typing') {
        const M = marqo();
        if (!M) return; // no Marqo on the page → leave the native form submit
        const seq = ++this.#reqSeq;
        const [res, suggestions] = await Promise.all([
          // fetch a larger pool than the grid shows so the fallback term-miner
          // has enough product titles to work with; the grid is sliced in #render.
          M.search({ q, limit: SUGGEST_POOL, offset: 0, facets: M.DEFAULT_FACETS, attributesToRetrieve: ATTRS })
            .catch((err) => { console.error(`[${ELEMENT_NAME}] search failed`, err); return null; }),
          mode === 'default' ? Promise.resolve(this.#popular()) : this.#fetchSuggestions(q),
        ]);
        if (seq !== this.#reqSeq) return; // a newer keystroke superseded this response
        // Real query suggestions when the index has them; otherwise derive from
        // the facet dictionary (brands/categories/…) + the current result titles.
        let terms = suggestions;
        if (mode !== 'default' && !terms.length) terms = await this.#deriveSuggestions(res, q);
        if (seq !== this.#reqSeq) return; // dictionary fetch may have raced a newer keystroke
        this.#render(res, terms, q, mode);
      }

      #popular() {
        return String(this.dataset.popular || '').split(',').map((s) => s.trim()).filter(Boolean);
      }

      // Catalog-wide facet values (brands, categories, types, sizes, …) fetched
      // once and cached, so a partial query can prefix-match "Honda" even when
      // the product search for "hon" doesn't surface Honda products itself.
      #loadDictionary() {
        if (this.#dict) return Promise.resolve(this.#dict);
        if (this.#dictPromise) return this.#dictPromise;
        const M = marqo();
        if (!M) return Promise.resolve([]);
        this.#dictPromise = M.search({ q: '*', limit: 1, facets: M.DEFAULT_FACETS, attributesToRetrieve: ['_id'] })
          .then((res) => {
            const facets = (res && res.facets) || {};
            const seen = new Map();
            Object.keys(facets).forEach((field) => {
              if (field === 'price') return;
              facetEntries(facets[field]).forEach(([v, info]) => {
                const t = String(v || '').trim();
                if (!t) return;
                const key = t.toLowerCase();
                const count = (info && (info.count ?? (info.value && info.value.count))) || 0;
                const cur = seen.get(key);
                if (cur) cur.count += count; else seen.set(key, { term: t, count });
              });
            });
            this.#dict = [...seen.values()];
            return this.#dict;
          })
          .catch(() => { this.#dict = []; return this.#dict; });
        return this.#dictPromise;
      }

      #fetchSuggestions(q) {
        const recs = marqo() && marqo().recommendations;
        if (!recs || typeof recs.queries !== 'function') {
          console.warn(`[${ELEMENT_NAME}] Marqo.recommendations.queries unavailable`);
          return Promise.resolve([]);
        }
        return recs.queries({ q, limit: SUGGEST_MAX })
          .then((r) => this.#extractSuggestions(r))
          .catch((err) => { console.warn(`[${ELEMENT_NAME}] queries() failed`, err); return []; });
      }

      // Term suggestions arrive in a server-defined shape; tolerate the common
      // container keys and item shapes (string, or object with a text-ish key).
      #extractSuggestions(r) {
        if (!r) return [];
        const arr = Array.isArray(r) ? r
          : (r.suggestions || r.results || r.queries || r.terms || r.options
             || (r.data && (r.data.suggestions || r.data.results || r.data.queries)) || []);
        if (!Array.isArray(arr)) return [];
        return arr.map((it) => this.#suggestText(it)).filter(Boolean);
      }

      #render(res, terms, q, mode = 'typing') {
        const isDefault = mode === 'default';
        const hits = (res && res.hits) || [];

        // Suggestions column: term completions while typing (thumbnail + matched
        // term), or the shopper's Recent Searches in the default (empty) view.
        const title = this.querySelector('[data-suggest-title]');
        if (isDefault) {
          if (title) title.textContent = 'Recent Searches';
          this.#renderSuggestions(readRecent(), '', hits, true);
        } else {
          if (title) title.textContent = 'Search Suggestions';
          this.#renderSuggestions(terms, q, hits, false);
        }

        // Trending column: configured Popular Searches, else top catalog facets.
        this.#renderTrending(res);

        // Recommended Products grid — 5 cards + a "See more" tile (2 rows of 3).
        const gridHits = hits.slice(0, 5);
        this.#grid.innerHTML = '';
        this.#setActive(-1);
        if (gridHits.length) {
          const frag = marqo().buildCard(gridHits, this.#cardTemplate);
          frag.querySelectorAll('.marqo-product-card').forEach((el, i) => {
            el.id = `dev-ps-opt-${i}`;
            el.setAttribute('role', 'option');
          });
          this.#hydrateImages(frag);
          this.#grid.appendChild(frag);
          this.#grid.appendChild(this.#buildSeeMore(q));
        }
        this.#empty.hidden = hits.length > 0;

        this.#open();
      }

      // Suggestion rows (Figma "Search suggestion"): a 45px product thumbnail + the
      // term with the matched substring underlined. `plain` (recent searches) skips
      // the thumbnail/underline. Thumbnails come from the best-matching product hit.
      #renderSuggestions(list, q, hits, plain) {
        const group = this.querySelector('[data-suggest-group]');
        const host = this.querySelector('[data-suggestions]');
        if (!group || !host) return;
        const terms = [];
        (list || []).forEach((it) => {
          const t = this.#suggestText(it);
          if (t && !terms.some((x) => x.toLowerCase() === t.toLowerCase())) terms.push(t);
        });
        if (!terms.length) { group.hidden = true; host.innerHTML = ''; return; }
        const base = this.dataset.searchUrl || '/search';
        host.innerHTML = terms.slice(0, SUGGEST_MAX).map((term, i) => {
          const href = `${base}?q=${encodeURIComponent(term)}`;
          const thumb = plain ? '' : this.#suggestThumb(term, hits, i);
          const label = plain ? escapeHtml(term) : this.#markMatch(term, q);
          return `<a class="dev-ps__suggest" data-suggest-term="${escapeAttr(term)}" href="${href}">`
            + thumb
            + `<span class="dev-ps__suggest-text">${label}</span></a>`;
        }).join('');
        group.hidden = false;
      }

      // Underline the occurrence of the typed query inside the suggestion (the top
      // completion equals the query, so it underlines whole); no match → plain text.
      #markMatch(term, q) {
        const t = String(term);
        const query = String(q || '').trim();
        if (!query) return escapeHtml(t);
        const idx = t.toLowerCase().indexOf(query.toLowerCase());
        if (idx < 0) return escapeHtml(t);
        return escapeHtml(t.slice(0, idx))
          + `<span class="dev-ps__match">${escapeHtml(t.slice(idx, idx + query.length))}</span>`
          + escapeHtml(t.slice(idx + query.length));
      }

      // A thumbnail for a suggestion row: the first product whose title contains the
      // term, else the i-th hit as a loose visual pairing. parseHit() resolves the
      // same image buildCard() uses. Empty circle when nothing is available.
      #suggestThumb(term, hits, i) {
        const M = marqo();
        const list = hits || [];
        const tl = String(term).toLowerCase();
        let hit = list.find((h) => String((h && (h.productTitle || h.name || h.title)) || '').toLowerCase().includes(tl));
        if (!hit) hit = list[i] || list[0];
        let img = '';
        try { img = hit && M && M.parseHit ? M.parseHit(hit).image : ''; } catch (_) { img = ''; }
        if (!img) return '<span class="dev-ps__suggest-thumb" aria-hidden="true"></span>';
        return `<span class="dev-ps__suggest-thumb" aria-hidden="true"><img src="${escapeAttr(img)}" alt="" loading="lazy"></span>`;
      }

      // Trending chips: the configured Popular Searches, else the top Category/Brand
      // facet values so the column is never empty. Each links to the search page.
      #renderTrending(res) {
        const group = this.querySelector('[data-trending-group]');
        const host = this.querySelector('[data-trending]');
        if (!group || !host) return;
        let terms = this.#popular();
        if (!terms.length && res && res.facets) {
          RAIL_FACETS.forEach(({ field }) => {
            facetEntries(res.facets[field]).slice(0, 3).forEach(([v]) => { if (v) terms.push(v); });
          });
        }
        terms = [...new Set(terms.map((t) => String(t).trim()).filter(Boolean))].slice(0, 8);
        if (!terms.length) { group.hidden = true; host.innerHTML = ''; return; }
        const base = this.dataset.searchUrl || '/search';
        host.innerHTML = terms.map((term) =>
          `<a class="dev-ps__chip" data-suggest-term="${escapeAttr(term)}" href="${base}?q=${encodeURIComponent(term)}">${escapeHtml(term)}</a>`
        ).join('');
        group.hidden = false;
      }

      // "See more" tile appended as the last product cell → the search results page.
      #buildSeeMore(q) {
        const base = this.dataset.searchUrl || '/search';
        const a = document.createElement('a');
        a.className = 'dev-ps-card dev-ps-card--more';
        a.href = q ? `${base}?q=${encodeURIComponent(q)}` : base;
        a.setAttribute('data-view-all', '');
        a.innerHTML = '<span class="dev-ps-more__icon" aria-hidden="true">'
          + '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.4"><path d="M12 5v14M5 12h14" stroke-linecap="round"/></svg>'
          + '</span><span class="dev-ps-more__label">See more</span>';
        return a;
      }

      // Fallback term suggestions when the real queries index is empty. Prefix-
      // match the typed text against the cached catalog facet dictionary (so
      // "hon" → "Honda" even if the product search doesn't return Honda), plus
      // every word across the current result titles; rank by frequency/weight.
      async #deriveSuggestions(res, q) {
        const ql = String(q).toLowerCase();
        const dict = await this.#loadDictionary();
        const freq = new Map(); // key → { term, count }
        const bump = (raw, weight) => {
          const t = String(raw || '').trim();
          const key = t.toLowerCase();
          if (!t || key === ql || key.length < ql.length || !key.startsWith(ql)) return;
          const cur = freq.get(key);
          if (cur) cur.count += weight; else freq.set(key, { term: t, count: weight });
        };
        // facet dictionary first (weighted high so brands/categories lead)
        dict.forEach((d) => bump(d.term, 1000 + (d.count || 0)));
        // then keyword completions from the current result titles
        ((res && res.hits) || []).forEach((h) => {
          String((h && (h.productTitle || h.name || h.title)) || '')
            .split(/[\s/,&|()-]+/)
            .forEach((w) => bump(w.replace(/[^\w]/g, ''), 1));
        });
        return [...freq.values()].sort((a, b) => b.count - a.count).slice(0, SUGGEST_MAX).map((x) => x.term);
      }

      #suggestText(item) {
        if (typeof item === 'string') return item;
        if (!item || typeof item !== 'object') return '';
        return item.text || item.query || item.term || item.value || item.suggestion
          || item.phrase || item.label || item.q || item.completion || item.keyword || '';
      }

      // Empty input / focus → the default view: run the search for the first Popular
      // Search so Recommended Products + Trending are populated (Recent Searches fill
      // the suggestions column). Falls back to a recent-only panel when no popular
      // searches are configured, and closes when there's nothing to show.
      #showDefault() {
        const popular = this.#popular();
        if (popular.length) { this.#search(popular[0], 'default'); return; }
        const recent = readRecent();
        if (!recent.length) { this.#close(); return; }
        const title = this.querySelector('[data-suggest-title]');
        if (title) title.textContent = 'Recent Searches';
        this.#renderSuggestions(recent, '', [], true);
        this.#renderTrending(null);
        this.#grid.innerHTML = '';
        this.#empty.hidden = true;
        this.#open();
      }

      #hydrateImages(root) {
        root.querySelectorAll('img[data-src]').forEach((img) => {
          if (!img.getAttribute('src') && img.dataset.src) img.src = img.dataset.src;
        });
      }

      #open() {
        const wasHidden = this.#panel.hidden;
        this.#panel.hidden = false;
        this.#input.setAttribute('aria-expanded', 'true');
        // Modal mode: the fixed panel covers the navbar, the scrim catches outside
        // clicks, and the page scroll is locked while open.
        this.classList.add('is-search-open');
        if (this.#scrim) this.#scrim.hidden = false;
        document.body.style.overflow = 'hidden';
        this.#sizePanel();
        // On the first open, hand typing to the pill field (the visible input over the
        // now-covered navbar search). Only on the hidden→shown edge so re-renders while
        // typing don't steal the caret.
        if (wasHidden && this.#pillInput && document.activeElement !== this.#pillInput) {
          this.#pillInput.focus();
          try { const p = this.#pillInput; p.setSelectionRange(p.value.length, p.value.length); } catch (_) { /* noop */ }
        }
      }

      // The fixed panel drops in just under the topbar (covering the navbar). Anchor
      // its top to the navbar's offset and cap the height to the viewport; the panel
      // scrolls the rest (overflow-y:auto).
      #sizePanel() {
        if (this.#panel.hidden) return;
        const nav = this.closest('.dev-navbar');
        const top = nav ? Math.max(0, nav.getBoundingClientRect().top) : 0;
        this.#panel.style.top = `${top}px`;
        this.#panel.style.maxHeight = `${Math.max(200, window.innerHeight - top)}px`;
      }

      #close() {
        this.#panel.hidden = true;
        this.#input.setAttribute('aria-expanded', 'false');
        this.classList.remove('is-search-open');
        if (this.#scrim) this.#scrim.hidden = true;
        document.body.style.overflow = '';
        this.#setActive(-1);
      }

      #items() { return [...this.#grid.querySelectorAll('.marqo-product-card')]; }

      #setActive(i) {
        const items = this.#items();
        items.forEach((el) => el.classList.remove('is-active'));
        this.#activeIndex = i;
        if (i >= 0 && items[i]) {
          items[i].classList.add('is-active');
          this.#input.setAttribute('aria-activedescendant', items[i].id);
          items[i].scrollIntoView({ block: 'nearest' });
        } else {
          this.#input.removeAttribute('aria-activedescendant');
        }
      }

      #onKeydown(e) {
        if (this.#panel.hidden) return;
        const items = this.#items();
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          this.#setActive(items.length ? Math.min(this.#activeIndex + 1, items.length - 1) : -1);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          this.#setActive(Math.max(this.#activeIndex - 1, 0));
        } else if (e.key === 'Enter') {
          const active = items[this.#activeIndex];
          const link = active && active.querySelector('a[href]');
          if (link) { e.preventDefault(); pushRecent(this.#input.value); window.location.href = link.href; return; }
          // No highlighted item → go to the full results page for the typed query
          // (works from the pill, which has no form of its own). Junk is blocked.
          e.preventDefault();
          const q = String((e.target && e.target.value) || this.#input.value || '').trim();
          if (q && HAS_TERM.test(q)) {
            pushRecent(q);
            const base = this.dataset.searchUrl || '/search';
            window.location.href = `${base}?q=${encodeURIComponent(q)}`;
          }
        } else if (e.key === 'Escape') {
          this.#close();
          this.#input.blur();
          if (this.#pillInput) this.#pillInput.blur();
        }
      }
    });
  }
})();

/**
 * Keep the header cart-count badge (.dev-navbar__cart-count) in sync with cart
 * changes. Section morphs only reliably update the badge on add-to-cart; quantity
 * changes/removals morph the drawer (hydration mode) or the cart-page section, so
 * we listen for cart:update directly and read the new item count from the event
 * (with a /cart.js fallback).
 */
(function initDevCartCountBadge() {
  // Header (desktop + mobile) AND the mobile menu's Cart tile each render their
  // own .dev-navbar__cart-count — update every instance, not just the first.
  function setBadge(count) {
    const badges = document.querySelectorAll('.dev-navbar__cart-count');
    if (!badges.length) return;
    const n = Number(count) || 0;
    badges.forEach((badge) => {
      badge.textContent = n;
      badge.toggleAttribute('hidden', n <= 0);
    });
  }

  document.addEventListener('cart:update', (event) => {
    const detail = event && event.detail;
    let count =
      detail?.data?.itemCount ??
      detail?.resource?.item_count ??
      detail?.data?.resource?.item_count;

    if (count == null) {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((cart) => setBadge(cart.item_count))
        .catch(() => {});
      return;
    }
    setBadge(count);
  });
})();
